import { useState, useRef, useEffect } from "react";
import "../css/Header.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useClickOutside } from "primereact/hooks";
import Divider from "@mui/material/Divider";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import { useNavigate } from "react-router-dom";
import { toast } from "./Toast/toast.ts";
import { useNotificationContext } from "../context/NotificationContext.tsx";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import QuestionAnswerRoundedIcon from "@mui/icons-material/QuestionAnswerRounded";

const getNotificationIcon = (item: any) => {
  if (item?.store_id)        return <StorefrontRoundedIcon sx={{ fontSize: 18 }} />;
  if (item?.evidence_id)     return <FolderSpecialRoundedIcon sx={{ fontSize: 18 }} />;
  if (item?.report_id)       return <AssessmentRoundedIcon sx={{ fontSize: 18 }} />;
  if (item?.custom_query_id) return <QuestionAnswerRoundedIcon sx={{ fontSize: 18 }} />;
  return <NotificationsActiveRoundedIcon sx={{ fontSize: 18 }} />;
};

export default function NotificationContainer() {
  const navigate = useNavigate();
  const storedToken = localStorage.getItem("token");
  const { readNotification, setReadNotification } = useNotificationContext();
  const [notificationData, setNotificationData] = useState<any[]>([]);
  const [reloadData, setReloadData] = useState(false);
  const [openNot, setOpenNot] = useState(false);
  const closeNotification = useRef(null);

  useClickOutside(closeNotification, () => {
    setOpenNot(false);
  });

  const fetchUserData = async () => {
    try {
      if (!storedToken) return;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/cust-portal/customer/notification`,
        {
          headers: {
            Authorization: `Bearer ${storedToken}`,
          },
        },
      );

      setNotificationData(res?.data || []);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to retrieve notifications!",
      );
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [reloadData]);

  const NotificationRead = async (id: string) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/cust-portal/customer/notification`,
        {
          notification_id: id,
        },
        {
          headers: {
            Authorization: `Bearer ${storedToken}`,
          },
        },
      );

      setReadNotification(!readNotification);
      setReloadData((prev) => !prev);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "An error occurred.",
      );
    }
  };

  const handleOpenRead = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/cust-portal/customer/notification/mark-all-read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${storedToken}`,
          },
        },
      );

      setReloadData((prev) => !prev);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to mark all as read",
      );
    }
  };

  const handleClick = (item: any) => {
    if (item?.store_id) {
      navigate("/manage-stores");
    } else if (item?.report_id) {
      navigate(`/report/${item?.report_id}`);
    } else if (item?.evidence_id) {
      navigate(`/evidence-request/${item?.evidence_id}`);
    } else if (item?.custom_query_id) {
      navigate(`/custom-request/${item?.custom_query_id}`);
    } else {
      navigate("/home");
    }

    if (!item.is_read) NotificationRead(item?._id);

    setOpenNot(false);
  };

  const unreadCount =
    notificationData?.filter((item) => !item.is_read)?.length || 0;

  return (
    <>
      {/* TRIGGER */}

      <motion.button
        whileTap={{ scale: 0.96 }}
        whileHover={{ scale: 1.04 }}
        className="notification-trigger"
        onClick={() => {
          if (!openNot) fetchUserData();
          setOpenNot(!openNot);
        }}
      >
        <NotificationsRoundedIcon className="notification-bell-icon" />

        {unreadCount > 0 && (
          <motion.div
            layout
            initial={{
              scale: 0,
            }}
            animate={{
              scale: 1,
            }}
            className="notification-badge"
          >
            {unreadCount}
          </motion.div>
        )}
      </motion.button>

      {/* POPOVER */}

      <AnimatePresence>
        {openNot && (
          <motion.div
            ref={closeNotification}
            className="notification-panel"
            initial={{
              opacity: 0,
              y: -12,
              scale: 0.96,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              y: -8,
              scale: 0.98,
            }}
            transition={{
              duration: 0.22,
              ease: "easeOut",
            }}
          >
            {/* HEADER */}

            <div className="notification-header">
              <div className="notification-header-left">
                <NotificationsRoundedIcon />

                <div className="flex flex-col" >
                  <h3 className="card-title" >Notifications</h3>

                  {unreadCount > 0 && <p className="card-subtitle">{unreadCount} unread updates</p>}
                </div>
              </div>

              <button
                className="notification-close-btn"
                onClick={() => setOpenNot(false)}
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>

            <Divider />

            {/* ACTIONS */}

            <div className="notification-actions">
              <button className="active-filter">All Notifications</button>

              {!!unreadCount && (
                <button className="mark-read-btn" onClick={handleOpenRead}>
                  <DoneAllRoundedIcon
                    sx={{
                      fontSize: 16,
                    }}
                  />
                  Mark all read
                </button>
              )}
            </div>

            {/* CONTENT */}

            <div className="notification-scroll">
              {notificationData?.length > 0 ? (
                notificationData.map((item, index) => (
                   <NotifItem key={item._id} item={item} index={index} onClick={handleClick} />
                ))
              ) : (
                <motion.div
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: 1,
                  }}
                  className="notification-empty"
                >
                  <NotificationsRoundedIcon />

                  <h4>No Notifications</h4>

                  <p>You're all caught up.</p>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ─── NotifItem sub-component ─────────────────────────────────────────────── */

function NotifItem({
  item,
  index,
  onClick,
}: {
  item: any;
  index: number;
  onClick: (item: any) => void;
}) {

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      whileHover={{ y: -1 }}
      onClick={() => onClick(item)}
      className={`
        relative flex items-start gap-3 p-3 rounded-md cursor-pointer mb-1
        border transition-all duration-150
        ${
          !item.is_read
            ? "bg-gold-2/10 border-transparent hover:bg-[rgb(var(--surface-2))] hover:border-[rgb(var(--border-primary))]"
            : "border-transparent hover:bg-[rgb(var(--surface-2))] hover:border-[rgb(var(--border-primary))]"
        }
      `}
    >

      {/* icon */}
      <div className="notification-icon-wrapper">
        {getNotificationIcon(item)}
      </div>

      {/* body */}
      <div className="flex-1 min-w-0">
        <p
          className={`
          text-[12.5px] leading-[1.5] m-0
          ${
            !item.is_read
              ? "text-[rgb(var(--text-primary))] font-medium"
              : "text-[rgb(var(--text-secondary))] font-normal"
          }
        `}
        >
          {item?.notificationMessage}
        </p>
      </div>

      {/* unread dot (right side) */}
      {!item.is_read && (
        <span className="w-2 h-2 mt-1 rounded-full bg-[rgb(var(--color-gold-2))] flex-shrink-0" />
      )}
    </motion.div>
  );
}
